
import React, { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  getNamePrefixes, 
  getNameSuffixes, 
  getLastNames,
  addNamePrefix,
  addNameSuffix,
  addLastName,
  deleteNamePrefix,
  deleteNameSuffix,
  deleteLastName,
  importNamePrefixes,
  importNameSuffixes,
  importLastNames
} from "@/components/tools/elf-name-generator/nameApi";
import { NamePrefix, NameSuffix, LastName } from "@/components/tools/elf-name-generator/types";
import NamesList from "./NamesList";
import BulkImport from "./BulkImport";
import { useToast } from "@/hooks/use-toast";

const NameManager: React.FC = () => {
  const { toast } = useToast();
  
  // State for name components
  const [prefixes, setPrefixes] = useState<NamePrefix[]>([]);
  const [suffixes, setSuffixes] = useState<NameSuffix[]>([]);
  const [lastNames, setLastNames] = useState<LastName[]>([]);
  
  // Loading states
  const [loadingPrefixes, setLoadingPrefixes] = useState(true);
  const [loadingSuffixes, setLoadingSuffixes] = useState(true);
  const [loadingLastNames, setLoadingLastNames] = useState(true);
  
  // Current tab
  const [activeTab, setActiveTab] = useState("prefixes");
  const [importTab, setImportTab] = useState("prefixes");

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load prefixes
        setLoadingPrefixes(true);
        const prefixData = await getNamePrefixes();
        setPrefixes(prefixData);
        setLoadingPrefixes(false);
        
        // Load suffixes
        setLoadingSuffixes(true);
        const suffixData = await getNameSuffixes();
        setSuffixes(suffixData);
        setLoadingSuffixes(false);
        
        // Load last names
        setLoadingLastNames(true);
        const lastNameData = await getLastNames();
        setLastNames(lastNameData);
        setLoadingLastNames(false);
      } catch (error) {
        console.error("Error loading name components:", error);
        toast({
          title: "Error",
          description: "Failed to load name components",
          variant: "destructive"
        });
      }
    };
    
    loadData();
  }, [toast]);

  // Map database fields to component-expected fields
  const mappedPrefixes = prefixes.map(p => ({
    id: p.id,
    name: p.prefix,
    category: p.category
  }));

  const mappedSuffixes = suffixes.map(s => ({
    id: s.id,
    name: s.suffix,
    category: s.category
  }));

  const mappedLastNames = lastNames.map(ln => ({
    id: ln.id,
    name: ln.last_name,
    category: ln.category
  }));

  // Handle adding name components
  const handleAddPrefix = async (prefix: string, category?: string) => {
    try {
      const success = await addNamePrefix(prefix, category);
      if (success) {
        // Reload prefixes
        const prefixData = await getNamePrefixes();
        setPrefixes(prefixData);
        toast({
          title: "Success",
          description: "Prefix added successfully"
        });
      } else {
        throw new Error("Failed to add prefix");
      }
    } catch (error) {
      console.error("Error adding prefix:", error);
      toast({
        title: "Error",
        description: "Failed to add prefix",
        variant: "destructive"
      });
    }
  };

  const handleAddSuffix = async (suffix: string, category?: string) => {
    try {
      const success = await addNameSuffix(suffix, category);
      if (success) {
        // Reload suffixes
        const suffixData = await getNameSuffixes();
        setSuffixes(suffixData);
        toast({
          title: "Success",
          description: "Suffix added successfully"
        });
      } else {
        throw new Error("Failed to add suffix");
      }
    } catch (error) {
      console.error("Error adding suffix:", error);
      toast({
        title: "Error",
        description: "Failed to add suffix",
        variant: "destructive"
      });
    }
  };

  const handleAddLastName = async (lastName: string, category?: string) => {
    try {
      const success = await addLastName(lastName, category);
      if (success) {
        // Reload last names
        const lastNameData = await getLastNames();
        setLastNames(lastNameData);
        toast({
          title: "Success",
          description: "Last name added successfully"
        });
      } else {
        throw new Error("Failed to add last name");
      }
    } catch (error) {
      console.error("Error adding last name:", error);
      toast({
        title: "Error",
        description: "Failed to add last name",
        variant: "destructive"
      });
    }
  };

  // Handle deleting name components
  const handleDeletePrefix = async (id: string) => {
    try {
      const success = await deleteNamePrefix(id);
      if (success) {
        // Update local state without reload
        setPrefixes(prefixes.filter(p => p.id !== id));
        toast({
          title: "Success",
          description: "Prefix deleted successfully"
        });
      } else {
        throw new Error("Failed to delete prefix");
      }
    } catch (error) {
      console.error("Error deleting prefix:", error);
      toast({
        title: "Error",
        description: "Failed to delete prefix",
        variant: "destructive"
      });
    }
  };

  const handleDeleteSuffix = async (id: string) => {
    try {
      const success = await deleteNameSuffix(id);
      if (success) {
        // Update local state without reload
        setSuffixes(suffixes.filter(s => s.id !== id));
        toast({
          title: "Success",
          description: "Suffix deleted successfully"
        });
      } else {
        throw new Error("Failed to delete suffix");
      }
    } catch (error) {
      console.error("Error deleting suffix:", error);
      toast({
        title: "Error",
        description: "Failed to delete suffix",
        variant: "destructive"
      });
    }
  };

  const handleDeleteLastName = async (id: string) => {
    try {
      const success = await deleteLastName(id);
      if (success) {
        // Update local state without reload
        setLastNames(lastNames.filter(ln => ln.id !== id));
        toast({
          title: "Success",
          description: "Last name deleted successfully"
        });
      } else {
        throw new Error("Failed to delete last name");
      }
    } catch (error) {
      console.error("Error deleting last name:", error);
      toast({
        title: "Error",
        description: "Failed to delete last name",
        variant: "destructive"
      });
    }
  };

  // Handle bulk import
  const handleImportPrefixes = async (items: {name: string, category?: string}[]) => {
    try {
      const result = await importNamePrefixes(items);
      
      if (result.success > 0) {
        // Reload prefixes
        const prefixData = await getNamePrefixes();
        setPrefixes(prefixData);
        
        toast({
          title: "Import Complete",
          description: `${result.success} prefixes imported successfully${result.failed > 0 ? `, ${result.failed} failed` : ''}`
        });
      }
      
      return result;
    } catch (error) {
      console.error("Error importing prefixes:", error);
      toast({
        title: "Import Failed",
        description: "Failed to import prefixes",
        variant: "destructive"
      });
      return { success: 0, failed: items.length };
    }
  };

  const handleImportSuffixes = async (items: {name: string, category?: string}[]) => {
    try {
      const result = await importNameSuffixes(items);
      
      if (result.success > 0) {
        // Reload suffixes
        const suffixData = await getNameSuffixes();
        setSuffixes(suffixData);
        
        toast({
          title: "Import Complete",
          description: `${result.success} suffixes imported successfully${result.failed > 0 ? `, ${result.failed} failed` : ''}`
        });
      }
      
      return result;
    } catch (error) {
      console.error("Error importing suffixes:", error);
      toast({
        title: "Import Failed",
        description: "Failed to import suffixes",
        variant: "destructive"
      });
      return { success: 0, failed: items.length };
    }
  };

  const handleImportLastNames = async (items: {name: string, category?: string}[]) => {
    try {
      const result = await importLastNames(items);
      
      if (result.success > 0) {
        // Reload last names
        const lastNameData = await getLastNames();
        setLastNames(lastNameData);
        
        toast({
          title: "Import Complete",
          description: `${result.success} last names imported successfully${result.failed > 0 ? `, ${result.failed} failed` : ''}`
        });
      }
      
      return result;
    } catch (error) {
      console.error("Error importing last names:", error);
      toast({
        title: "Import Failed",
        description: "Failed to import last names",
        variant: "destructive"
      });
      return { success: 0, failed: items.length };
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Name Components</CardTitle>
          <CardDescription>
            Manage the name components used to generate elf names
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="prefixes">
                Prefixes ({loadingPrefixes ? "..." : prefixes.length})
              </TabsTrigger>
              <TabsTrigger value="suffixes">
                Suffixes ({loadingSuffixes ? "..." : suffixes.length})
              </TabsTrigger>
              <TabsTrigger value="lastNames">
                Last Names ({loadingLastNames ? "..." : lastNames.length})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="prefixes" className="mt-4">
              <NamesList
                title="Name Prefixes"
                items={mappedPrefixes}
                isLoading={loadingPrefixes}
                onAdd={handleAddPrefix}
                onDelete={handleDeletePrefix}
                nameField="prefix"
                placeholder="Add new prefix (e.g., Jolly, Twinkle)..."
              />
            </TabsContent>
            
            <TabsContent value="suffixes" className="mt-4">
              <NamesList
                title="Name Suffixes"
                items={mappedSuffixes}
                isLoading={loadingSuffixes}
                onAdd={handleAddSuffix}
                onDelete={handleDeleteSuffix}
                nameField="suffix"
                placeholder="Add new suffix (e.g., bell, shine)..."
              />
            </TabsContent>
            
            <TabsContent value="lastNames" className="mt-4">
              <NamesList
                title="Last Names"
                items={mappedLastNames}
                isLoading={loadingLastNames}
                onAdd={handleAddLastName}
                onDelete={handleDeleteLastName}
                nameField="last_name"
                placeholder="Add new last name (e.g., Sugarplum)..."
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Bulk Import</CardTitle>
          <CardDescription>
            Import multiple name components at once
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={importTab} onValueChange={setImportTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="prefixes">Prefixes</TabsTrigger>
              <TabsTrigger value="suffixes">Suffixes</TabsTrigger>
              <TabsTrigger value="lastNames">Last Names</TabsTrigger>
            </TabsList>
            
            <TabsContent value="prefixes" className="mt-4">
              <BulkImport
                onImport={handleImportPrefixes}
                nameField="prefix"
                placeholder="Enter prefixes to import (e.g., Jolly, Twinkle, Sparkle)"
              />
            </TabsContent>
            
            <TabsContent value="suffixes" className="mt-4">
              <BulkImport
                onImport={handleImportSuffixes}
                nameField="suffix"
                placeholder="Enter suffixes to import (e.g., bell, shine, glow)"
              />
            </TabsContent>
            
            <TabsContent value="lastNames" className="mt-4">
              <BulkImport
                onImport={handleImportLastNames}
                nameField="lastName"
                placeholder="Enter last names to import (e.g., Sugarplum, Mistletoe)"
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default NameManager;
